<div class="wrapper">
  @if (heat) {
    <div class="kpis flex flex-row gap-[12px] flex-wrap mt-[20px] mb-[20px]">
      <button type="button" class="kpi stroked-card" (click)="setStatus('open')" data-testid="kpi-open"><div class="n">{{ heat.open }}</div><div class="l">Open</div></button>
      <button type="button" class="kpi stroked-card" (click)="setParams({ band: 'critical', page: null })" data-testid="kpi-critical"><div class="n txt-critical">{{ heat.critical }}</div><div class="l">Critical</div></button>
      <button type="button" class="kpi stroked-card" (click)="setParams({ band: 'high', page: null })" data-testid="kpi-high"><div class="n txt-high">{{ heat.high }}</div><div class="l">High</div></button>
      <button type="button" class="kpi stroked-card" (click)="setStatus('pending')" data-testid="kpi-pending"><div class="n txt-medium">{{ heat.pending }}</div><div class="l">Pending approval</div></button>
      <div class="kpi stroked-card" data-testid="kpi-stale"><div class="n">{{ heat.stale }}</div><div class="l">No update in 60d</div></div>
    </div>
  }

  <div class="action-section flex flex-row-reverse my-[20px] w-full gap-[20px]">
    @if (canManage || canPropose) {
      <app-primary-button (handleClick)="create()" class="!w-[150px]" data-testid="risk-create">
        {{ canManage ? 'Add risk' : 'Propose risk' }}
      </app-primary-button>
    }
    <div class="download-button-container">
      <button (click)="openExport()" class="download-button" mat-icon-button matTooltip="Export to Excel" matTooltipClass="project-name-tooltip" aria-label="Export to Excel" data-testid="risk-export">
        <mat-icon svgIcon="download"></mat-icon>
      </button>
    </div>
    @if (canImport || canConfigure) {
      <div class="download-button-container">
        <button mat-icon-button [matMenuTriggerFor]="moreMenu" class="download-button" data-testid="risk-more-menu" aria-label="Risk register actions" matTooltip="Import and settings" matTooltipClass="project-name-tooltip">
          <mat-icon class="more-icon">more_horiz</mat-icon>
        </button>
      </div>
      <mat-menu #moreMenu="matMenu" class="risk-menu">
        @if (canImport) {
          <button mat-menu-item (click)="openImport()" data-testid="risk-import"><mat-icon svgIcon="upload"></mat-icon><span>Import from spreadsheet</span></button>
        }
        @if (canConfigure) {
          <button mat-menu-item (click)="openConfig()" data-testid="risk-config"><mat-icon svgIcon="map-filters"></mat-icon><span>Risk areas &amp; disciplines</span></button>
        }
      </mat-menu>
    }

    <!-- Heat map behind a toolbar icon (Figma trigger 32779:71974), opening a popover
         (Figma 32841:76053 container); a cell click filters and shows the cell chip. -->
    <div class="download-button-container">
      <button #heatTrigger="cdkOverlayOrigin" cdkOverlayOrigin (click)="toggleHeat(heatTrigger.elementRef.nativeElement)" [disabled]="!heat"
        class="download-button" mat-icon-button matTooltip="Risk map" matTooltipClass="project-name-tooltip" aria-label="Risk map" [attr.aria-expanded]="heatOpen" data-testid="risk-heat-map-button">
        <mat-icon svgIcon="heat-map"></mat-icon>
      </button>
    </div>
    <ng-template
      (overlayOutsideClick)="heatOpen = false"
      (detach)="heatOpen = false"
      (overlayKeydown)="onHeatKeydown($event)"
      [cdkConnectedOverlayOpen]="heatOpen && !!heat"
      [cdkConnectedOverlayOrigin]="heatTrigger"
      [cdkConnectedOverlayPositions]="heatPositions"
      [cdkConnectedOverlayDisableClose]="true"
      cdkConnectedOverlay>
      <div class="risk-heat-popover">
        <app-risk-heat-map [cells]="heat.cells" [selected]="filter.cell" (cellSelected)="onCell($event)" />
      </div>
    </ng-template>

    <div class="risk-filters flex flex-row gap-[20px] flex-1 mt-[7px] min-w-0">
      <!-- Status: Payapp's single-select pill (pay-app.component.html) -->
      <mat-form-field class="payapp-dropdown no-border" data-testid="risk-status-filter">
        <mat-select [value]="statusValue" (selectionChange)="setStatus($event.value)" class="panel" disableOptionCentering panelClass="payapp-list-panel">
          <mat-select-trigger><span class="payapp-trigger"><span class="payapp-name">{{ statusLabel }}</span></span></mat-select-trigger>
          @for (s of statusFilters; track s.value) {
            <mat-option [value]="s.value" [attr.data-testid]="'status-' + s.value">
              {{ s.label }}@if (s.value === 'pending' && heat?.pending) { ({{ heat.pending }}) }
            </mat-option>
          }
        </mat-select>
        <mat-icon class="suffix-icon" svgIcon="chevron-down" matSuffix />
      </mat-form-field>

      <!-- Filters (n): the shared URL-driven tabbed filter (warranty claims).
           fitTabs: at most 3 tabs fit the 320px popup (about 17px spare); a 4th needs a different layout. -->
      <app-tabbed-filter [groups]="filterGroups" [extraActiveCount]="extraFilterCount" [fitTabs]="true" data-testid="risk-filters">
        @if (!isContractor) {
          <div tabbedFilterFooter class="filter-footer">
            <mat-checkbox [checked]="filter.internal" (change)="setInternal($event.checked)" color="primary" data-testid="filter-internal">Internal only</mat-checkbox>
          </div>
        }
      </app-tabbed-filter>
      <!-- Sort: Payapp's two-state sort pill -->
      @if (sortLabel) {
        <div class="sort-dropdown-container" data-testid="risk-sort">
          <mat-select [value]="filter.sort" (selectionChange)="onSortColumnChange($event.value)" disableOptionCentering panelClass="rfi-sort-panel" class="sort-select">
            <mat-select-trigger>{{ 'Sort by: ' + sortLabel }}</mat-select-trigger>
            @for (c of sortColumns; track c.value) { <mat-option [value]="c.value" [attr.data-testid]="'sort-' + c.value">{{ c.label }}</mat-option> }
          </mat-select>
          <mat-icon class="sort-direction" (click)="toggleSortDirection(); $event.stopPropagation()" data-testid="sort-direction">
            {{ filter.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </mat-icon>
          <mat-icon class="sort-close" (click)="removeSort($event)" data-testid="sort-clear">close</mat-icon>
        </div>
      } @else {
        <mat-form-field class="sort-dropdown nosort" data-testid="risk-sort">
          <mat-select (selectionChange)="onSortColumnChange($event.value)" disableOptionCentering panelClass="rfi-sort-panel">
            @for (c of sortColumns; track c.value) { <mat-option [value]="c.value" [attr.data-testid]="'sort-' + c.value">{{ c.label }}</mat-option> }
          </mat-select>
          <div class="label text-type-8">Sort: </div>
          <mat-icon>sync_alt</mat-icon>
        </mat-form-field>
      }

      <div class="search-section flex flex-1 flex-row gap-[10px] min-w-0">
        <form [formGroup]="searchForm" class="flex-1 min-w-0">
          <div class="search w-full">
            <mat-icon svgIcon="search"></mat-icon>
            <input class="search-input" data-testid="risk-search" formControlName="searchText" matInput placeholder="Search" />
            @if (searchForm.controls.searchText.value) { <mat-icon (click)="clearSearch()" svgIcon="close"></mat-icon> }
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Band / heat-map cell come from the KPI tiles and heat map: removable chips
       (risk-config-dialog chip) on their own row, so the toolbar keeps its standard widths -->
  @if (bandChipLabel || filter.cell) {
    <div class="filter-chips flex flex-row flex-wrap items-center gap-[8px] mb-[16px]" data-testid="filter-chips">
      @if (bandChipLabel) {
        <span class="chip" data-testid="filter-chip-band">Band: {{ bandChipLabel }}
          <button type="button" class="x" (click)="removeBandChip()" [attr.aria-label]="'Remove ' + bandChipLabel + ' filter'" data-testid="filter-chip-band-remove">✕</button>
        </span>
      }
      @if (filter.cell) {
        <span class="chip" data-testid="filter-chip-cell">Impact {{ filter.cell.impact }} × Prob {{ filter.cell.probability }}
          <button type="button" class="x" (click)="removeCellChip()" aria-label="Remove heat map cell filter" data-testid="filter-chip-cell-remove">✕</button>
        </span>
      }
    </div>
  }

  <div class="list-section">
    @if (loading && !rows.length) { <div class="no-data text-type-8 mt-[80px]">Loading risks&hellip;</div> }
    @if (!loading && loadError) { <div class="no-data text-type-8 mt-[80px]">{{ loadError }}</div> }

    @if (!loadError && rows.length) {
      <div class="table-scroll-wrapper">
        <table [dataSource]="rows" class="mat-elevation-z8 table" mat-table>
          <ng-container matColumnDef="risk_no">
            <th *matHeaderCellDef mat-header-cell (click)="setSort('risk_no')" class="sortable"><span class="th-text">ID</span></th>
            <td *matCellDef="let row" mat-cell><div class="text-type-8">{{ row.risk_no }}</div></td>
          </ng-container>
          <ng-container matColumnDef="score">
            <th *matHeaderCellDef mat-header-cell (click)="setSort('score')" class="sortable"><span class="th-text">Score {{ filter.sort === 'score' ? (filter.dir === 'desc' ? '▾' : '▴') : '' }}</span></th>
            <td *matCellDef="let row" mat-cell>
              <!-- Score + trend in one pill: the arrow and delta since the last score change. -->
              <div class="score-chip rounded-full" [ngClass]="[bandMap[band(row)].bgColor, bandMap[band(row)].textColor]" [attr.data-testid]="'score-' + row.risk_no">
                {{ row.score }}@if (trend(row).cls !== 'trend-same') {<span class="trend" [attr.data-testid]="'trend-' + row.risk_no">{{ trend(row).arrow }}{{ trend(row).delta }}</span>}
              </div>
            </td>
          </ng-container>
          <ng-container matColumnDef="description">
            <th *matHeaderCellDef mat-header-cell><span class="th-text">Risk</span></th>
            <td *matCellDef="let row" mat-cell>
              <div class="text-type-11 desc">@if (row.internal) { <mat-icon class="lock" matTooltip="Internal: hidden from contractors" matTooltipClass="project-name-tooltip">lock</mat-icon> }<span #descEl class="desc-text" [matTooltip]="row.description" [matTooltipDisabled]="!isTruncated(descEl)" matTooltipPosition="above" matTooltipClass="project-name-tooltip">{{ row.description }}</span></div>
              @if (row.status !== 'open') { <div class="status-chip px-[14px] rounded-full mt-[4px]" [ngClass]="[statusMap[row.status].bgColor, statusMap[row.status].textColor]">{{ statusMap[row.status].label }}</div> }
            </td>
          </ng-container>
          <ng-container matColumnDef="areas">
            <th *matHeaderCellDef mat-header-cell><span class="th-text">Risk areas</span></th>
            <td *matCellDef="let row" mat-cell>@for (a of row.risk_areas; track a) { <span class="tag">{{ a }}</span> }</td>
          </ng-container>
          <ng-container matColumnDef="owner">
            <th *matHeaderCellDef mat-header-cell><span class="th-text">Owner</span></th>
            <td *matCellDef="let row" mat-cell>
              @if (row.owner) { <div #ownerEl class="owner-text text-type-11" [matTooltip]="owner(row)" [matTooltipDisabled]="!isTruncated(ownerEl)" matTooltipPosition="above" matTooltipClass="project-name-tooltip">{{ owner(row) }}</div> }
              @else { <span class="text-type-11 secondary-text">Unassigned</span> }
            </td>
          </ng-container>
          <ng-container matColumnDef="last_update">
            <th *matHeaderCellDef mat-header-cell (click)="setSort('last_update_at')" class="sortable"><span class="th-text">Last update</span></th>
            <td *matCellDef="let row" mat-cell>
              @if (lastUpdate(row)) { <div class="text-type-11">{{ lastUpdate(row) | date: 'MMM d, y' }}</div> }
              @else { <div class="text-type-11 secondary-text">Never</div> }
            </td>
          </ng-container>
          <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
          <tr data-testid="risk-row" (click)="rowClicked(row)" *matRowDef="let row; columns: displayedColumns" [class.highlighted-row]="row.id === selectedRow?.id" mat-row></tr>
        </table>
      </div>
    }

    @if (!loading && !loadError && !rows.length) {
      <div class="no-data text-type-8 mt-[80px]" data-testid="risk-empty">
        {{ hasFilters ? 'No risks match these filters.' : (canImport ? 'No risks yet. Add one, or import your existing register.' : 'No risks yet.') }}
      </div>
      @if (!hasFilters && canImport) {
        <div class="empty-actions flex flex-row justify-center mt-[24px]">
          <app-secondary-button (handleClick)="openImport()" class="!w-[240px]" data-testid="risk-empty-import">Import from spreadsheet</app-secondary-button>
        </div>
      }
    }

    @if (totalRecords > 25) {
      <app-paginator (pageChanged)="onPageChanged($event)" [itemsPerPage]="25" [maxVisiblePages]="5" [totalItems]="totalRecords" class="paginator block mt-[40px]" />
    }
  </div>
</div>
